import { useEffect, useRef } from 'react';

/**
 * Visibility-aware polling hook.
 *
 * Calls `callback` every `intervalMs` while `enabled` is true AND the page is
 * visible. Polling fully stops when the tab goes to the background or the
 * screen turns off, and fires immediately once on return to visibility
 * (then resumes the interval).
 */
const usePolling = (callback, intervalMs, enabled = true) => {
  const savedCallback = useRef(callback);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!enabled || !intervalMs) return undefined;

    let timerId = null;

    const tick = () => savedCallback.current();

    const start = () => {
      if (timerId === null) {
        timerId = setInterval(tick, intervalMs);
      }
    };

    const stop = () => {
      if (timerId !== null) {
        clearInterval(timerId);
        timerId = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        tick();
        start();
      } else {
        stop();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    if (document.visibilityState === 'visible') {
      start();
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      stop();
    };
  }, [intervalMs, enabled]);
};

export default usePolling;
